import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    IconButton,
    Paper,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    LinearProgress,
} from "@mui/material";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import { API_BASE } from "../../AppConfig";
import { fetchTimeout } from "../../components/Common";

const canadaGibsonFont = {
    fontFamily: "canada-type-gibson",
    "& .MuiInputBase-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiInputLabel-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiFormHelperText-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiMenuItem-root": {
        fontFamily: "canada-type-gibson",
    },
};

const dateTimeOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
};

export default function JobAuditTrail({ jobId, toggleOpen }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandAll, setExpandAll] = useState(false);
    const [expandedIndexes, setExpandedIndexes] = useState([]);

    useEffect(() => {
        if (!jobId) return;

        setLoading(true);
        setError(null);

        const fetchLogs = async () => {
            try {
                const endPoint = API_BASE() + `audit/${jobId}`;
                const res = await fetchTimeout(endPoint, {
                    method: "GET",
                });

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || `Request failed: ${res.status}`);
                }

                const data = await res.json();
                setEvents(data?.Events || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [jobId]);

    useEffect(() => {
        if (expandAll) {
            setExpandedIndexes(events.map((_, i) => i));
        } else {
            setExpandedIndexes([]);
        }
    }, [expandAll, events]);

    const handleClose = () => {
        toggleOpen(false);
    };

    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    top: 100,
                    left: 100,
                    zIndex: 1300,
                }}
            >
                <Draggable handle=".drag-handle" cancel="button">
                    <div>
                        <ResizableBox
                            width={1000}
                            height={500}
                            minConstraints={[800, 500]}
                            maxConstraints={[Infinity, Infinity]}
                            resizeHandles={["se"]}
                        >
                            <Paper
                                className="drag-handle"
                                elevation={6}
                                sx={{
                                    cursor: "move",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "auto",
                                    padding: 2,
                                }}
                            >
                                <Box sx={{ flexShrink: 0 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ ...canadaGibsonFont }}>
                                            JOB AUDIT TRAIL
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={expandAll}
                                                    color="primary"
                                                    onChange={() => setExpandAll(!expandAll)}
                                                    name="checkedA"
                                                />
                                            }
                                            label="Expand All"
                                        />
                                        <IconButton onClick={handleClose}>
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                                {loading ? (
                                    <LinearProgress />
                                ) : events.length === 0 ? (
                                    <Typography sx={{ marginTop: "60px" }}>No audit events found.</Typography>
                                ) : (
                                    events
                                        .slice()
                                        .reverse()
                                        .map((event, idx) => (
                                            <Accordion
                                                key={idx}
                                                expanded={expandedIndexes.includes(idx)}
                                                onChange={() => {
                                                    setExpandedIndexes((prev) =>
                                                        prev.includes(idx)
                                                            ? prev.filter((i) => i !== idx)
                                                            : [...prev, idx]
                                                    );
                                                }}
                                            >
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            width: "80%",
                                                        }}
                                                    >
                                                        <span style={{ display: "inline-block" }}>
                                                            {new Date(event.Timestamp).toLocaleString(
                                                                undefined,
                                                                dateTimeOptions
                                                            )}
                                                        </span>
                                                        <span style={{ display: "inline-block", fontWeight: "bold" }}>
                                                            {event.User}
                                                        </span>
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails style={{ overflow: "hidden" }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>
                                                                    <strong>Property</strong>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <strong>Old Value</strong>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <strong>New Value</strong>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {Object.entries(event.Diffs).map(([field, diff], i) => {
                                                                const renderValue = (val, color) => {
                                                                    if (typeof val !== "string")
                                                                        return (
                                                                            <span style={{ color }}>{String(val)}</span>
                                                                        );

                                                                    try {
                                                                        const parsed = JSON.parse(val);
                                                                        if (
                                                                            typeof parsed === "object" &&
                                                                            parsed !== null
                                                                        ) {
                                                                            return (
                                                                                <pre>
                                                                                    {JSON.stringify(parsed, null, 2)}
                                                                                </pre>
                                                                            );
                                                                        }

                                                                        return <span style={{ color }}>{val}</span>;
                                                                    } catch {
                                                                        return <span style={{ color }}>{val}</span>;
                                                                    }
                                                                };

                                                                return (
                                                                    <TableRow key={i}>
                                                                        <TableCell>{field}</TableCell>
                                                                        <TableCell>
                                                                            {renderValue(
                                                                                diff.OldValue == ""
                                                                                    ? "Blank"
                                                                                    : diff.OldValue,
                                                                                "gray"
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {renderValue(
                                                                                diff.NewValue == ""
                                                                                    ? "\u2014"
                                                                                    : diff.NewValue,
                                                                                "green"
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))
                                )}
                            </Paper>
                        </ResizableBox>
                    </div>
                </Draggable>
            </Box>
        </>
    );
}
